// ─── Triggerable Events ─────────────────────────────────────────────
// The allowlist behind the admin "Publish event" dialog.
//
// Publishing an event by hand is a real trigger, not a dry run: the worker
// that picks it up does the same work it does in production. So the dialog
// cannot publish an arbitrary name with arbitrary JSON — it can only publish
// what is listed here, and only with a payload that passes the schema below.
//
// Adding an entry is the deliberate act of saying "an operator may fire this
// on purpose". Both the dialog and `POST /admin/events/publish` read this
// list, so the two can never disagree about what is allowed.

import { z } from 'zod';
import { ChangelogSyncRequestedPayload } from '../events/changelog.js';
import { SyncAllRequestedPayload, TenantKpiPrecacheRequestedPayload } from '../events/sync.js';
import { TenantSyncRequestedPayloadSchema } from '../types/tenant-bq-sync.js';
import { MarketplaceEvents, SyncEvents, SystemEvents, type EventName } from './event-names.js';

/** How much a hand-published event disturbs, so the dialog can warn accordingly. */
export const TriggerableEventImpacts = {
    /** Observable only — no writes, no spend. */
    SAFE: 'safe',
    /** Runs real work: writes data, hits upstream APIs, or costs money. */
    HEAVY: 'heavy',
} as const;

export type TriggerableEventImpact =
    (typeof TriggerableEventImpacts)[keyof typeof TriggerableEventImpacts];

export interface TriggerableEvent {
    /** Published as-is; the Pub/Sub topic is derived from the name's prefix. */
    eventName: EventName;
    /** Validated in the dialog before sending and again on the API before publishing. */
    schema: z.ZodType<Record<string, unknown>>;
    /** What happens once a worker picks it up. */
    summary: string;
    /** Which service consumes it, so an operator knows whose logs to read. */
    handledBy: string;
    impact: TriggerableEventImpact;
    /** Pre-filled in the dialog so the common case is one click. */
    sample: Record<string, unknown>;
}

export const TRIGGERABLE_EVENTS: readonly TriggerableEvent[] = [
    {
        eventName: SystemEvents.HEALTH_PING,
        schema: z.object({}),
        summary: 'Round-trips through Pub/Sub and every worker without touching data.',
        handledBy: 'All workers',
        impact: TriggerableEventImpacts.SAFE,
        sample: {},
    },
    {
        eventName: SystemEvents.ADMIN_OPS_SWEEP_REQUESTED,
        schema: z.object({}),
        summary:
            'Prunes history past its retention window and raises any ops alert that is due. ' +
            'Skips itself if the schedule already swept within the hour.',
        handledBy: 'users',
        // It deletes history and posts to Slack. Both are what it is for, and
        // both are things an operator should mean to do.
        impact: TriggerableEventImpacts.HEAVY,
        sample: {},
    },
    {
        eventName: SystemEvents.CHANGELOG_SYNC_REQUESTED,
        schema: ChangelogSyncRequestedPayload,
        summary:
            'Reads GitHub Releases, queues notes for new deploys, and posts any Slack notes ' +
            'that have come due. The API creates Cloud Scheduler job `changelog-sync` ' +
            '(`*/10 * * * *`) on boot when SCHEDULER_BACKEND=gcp.',
        handledBy: 'api',
        impact: TriggerableEventImpacts.HEAVY,
        sample: {},
    },
    {
        eventName: SyncEvents.TENANT_KPI_PRECACHE_REQUESTED,
        schema: TenantKpiPrecacheRequestedPayload,
        summary: "Rebuilds one tenant's dashboard KPI snapshots in the cache.",
        handledBy: 'data-sync',
        impact: TriggerableEventImpacts.HEAVY,
        sample: { tenantId: '' },
    },
    {
        eventName: SyncEvents.TENANT_SYNC_REQUESTED,
        schema: TenantSyncRequestedPayloadSchema,
        summary: "Syncs one tenant's industries, products, and personas from BigQuery.",
        handledBy: 'data-sync',
        impact: TriggerableEventImpacts.HEAVY,
        sample: { tenantId: '' },
    },
    {
        eventName: SyncEvents.ALL_REQUESTED,
        schema: SyncAllRequestedPayload,
        summary: 'Syncs every tenant from BigQuery — long-running and upstream-heavy.',
        handledBy: 'data-sync',
        impact: TriggerableEventImpacts.HEAVY,
        sample: {},
    },
    {
        eventName: MarketplaceEvents.OVERDUE_ORDERS_SWEEP_REQUESTED,
        schema: z.object({}),
        summary:
            'Posts the daily overdue marketplace orders digest to #marketplace-orders when any active ' +
            'order is past due. Skips when another sweep already claimed today (Redis). Provision via ' +
            'Admin → Scheduled Jobs: jobId `marketplace-overdue-orders-sweep`, cron `0 * * * *`, ' +
            'timezone `Etc/UTC`, event MARKETPLACE.OVERDUE_ORDERS_SWEEP_REQUESTED. Toggle with ' +
            'Marketplace Admin → Slack notifications → Daily overdue digest.',
        handledBy: 'marketplace',
        impact: TriggerableEventImpacts.HEAVY,
        sample: {},
    },
] as const;

export function findTriggerableEvent(eventName: string): TriggerableEvent | undefined {
    return TRIGGERABLE_EVENTS.find((entry) => entry.eventName === eventName);
}

/**
 * Started only by an admin pressing a button in the app, never from Admin Events or a schedule.
 * Kept apart from the catalog so Scheduled Jobs still refuses them, including as an Advanced target.
 */
const MANUAL_ONLY_EVENTS: ReadonlySet<string> = new Set([SyncEvents.TENANT_ONBOARDING_SYNC_REQUESTED]);

/** The templates Scheduled Jobs offers: every triggerable event except the manual-only ones. */
export const SCHEDULABLE_EVENTS: readonly TriggerableEvent[] = TRIGGERABLE_EVENTS.filter(
    (entry) => !MANUAL_ONLY_EVENTS.has(entry.eventName),
);

/** True for an event only an admin may start from its own screen; Scheduled Jobs refuses it in any form. */
export function isManualOnlyEvent(eventName: string): boolean {
    return MANUAL_ONLY_EVENTS.has(eventName);
}
